/**
 * CrudShell — Patrón unificado para todos los CRUD del Admin.
 *
 * Estructura:
 *   <CrudShell header toolbar table sidebar>
 *     CrudShellHeader  — título + badge + acción primaria
 *     CrudShellToolbar — búsqueda + filtros + acciones secundarias
 *     CrudShellTable   — DataGrid con EmptyState
 *     CrudShellDrawer  — panel lateral de edición / detalle
 *
 * Uso:
 *   <CrudShell>
 *     <CrudShellHeader title="Sucursales" count={items.length} action={<Button>Nueva</Button>} />
 *     <CrudShellToolbar search={...} filters={...} />
 *     <CrudShellTable columns={...} rows={...} />
 *     <CrudShellDrawer open={!!selected} onClose={() => setSelected(null)} title="Editar sucursal">
 *       ...form content...
 *     </CrudShellDrawer>
 *   </CrudShell>
 */
import React, { createElement, isValidElement, type ElementType, type ReactNode, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, X, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { lifecycleStatusLabel } from "@/lib/product-surface";

// ─── Root ────────────────────────────────────────────────────────────────────

interface CrudShellProps {
  children: ReactNode;
  className?: string;
}

export function CrudShell({ children, className }: CrudShellProps) {
  return (
    <div className={cn("flex flex-col gap-0 h-full", className)}>
      {children}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

interface CrudShellHeaderProps {
  title: string;
  description?: string;
  /** Alias de `description` para compatibilidad con llamadas legacy. */
  subtitle?: string;
  count?: number;
  badge?: ReactNode;
  action?: ReactNode;
  /** Alias de `action` para compatibilidad con llamadas legacy. */
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  /** Ruta inequívoca de volver (hub padre). */
  backHref?: string;
  backLabel?: string;
  className?: string;
}

export function CrudShellHeader({ title, description, subtitle, count, badge, action, actions, breadcrumb, backHref, backLabel, className }: CrudShellHeaderProps) {
  const resolvedDescription = description ?? subtitle;
  const resolvedAction = action ?? actions;
  return (
    <div className={cn("flex items-start justify-between gap-4 px-6 py-4 border-b bg-background", className)}>
      <div className="flex flex-col gap-0.5 min-w-0">
        {backHref ? (
          <Link
            to={backHref}
            className="mb-1 inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {backLabel ?? "Volver"}
          </Link>
        ) : null}
        {breadcrumb && <div className="text-xs text-muted-foreground">{breadcrumb}</div>}
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-lg font-semibold leading-tight tracking-tight truncate">{title}</h1>
          {count !== undefined && (
            <Badge variant="secondary" className="font-mono text-xs tabular-nums">{count}</Badge>
          )}
          {badge}
        </div>
        {resolvedDescription && <p className="text-sm text-muted-foreground leading-snug">{resolvedDescription}</p>}
      </div>
      {resolvedAction && <div className="shrink-0 flex items-center gap-2">{resolvedAction}</div>}
    </div>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────────────────────

interface CrudShellToolbarProps {
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
  };
  filters?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function CrudShellToolbar({ search, filters, actions, className }: CrudShellToolbarProps) {
  return (
    <div className={cn("flex items-center gap-2 px-6 py-2.5 border-b bg-background/80 backdrop-blur-sm flex-wrap", className)}>
      {search && (
        <div className="relative w-56 shrink-0">
          <Input
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? "Buscar…"}
            className="h-8 text-sm pl-3"
          />
          {search.value && (
            <button
              onClick={() => search.onChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {filters && <div className="flex items-center gap-2 flex-wrap">{filters}</div>}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────────────

interface CrudShellTableProps {
  children: ReactNode;
  className?: string;
}

export function CrudShellTable({ children, className }: CrudShellTableProps) {
  return (
    <div className={cn("flex-1 overflow-auto", className)}>
      {children}
    </div>
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────────────

interface CrudShellDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  help?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}

const DRAWER_WIDTH = {
  sm: "w-80",
  md: "w-[400px]",
  lg: "w-[520px]",
  xl: "w-[640px]",
};

export function CrudShellDrawer({ open, onClose, title, description, help, footer, children, width = "md" }: CrudShellDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-overlay z-40 transition-opacity"
          onClick={onClose}
          aria-hidden
        />
      )}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "fixed right-0 top-0 bottom-0 z-50 flex flex-col bg-card text-card-foreground border-l border-border shadow-xl transition-transform duration-200",
          DRAWER_WIDTH[width],
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0">
          <div className="flex flex-col gap-0.5 min-w-0">
            <h2 className="text-sm font-semibold leading-tight truncate">{title}</h2>
            {description && <p className="text-xs text-muted-foreground leading-snug">{description}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {help && (
              <button
                className="text-muted-foreground hover:text-foreground transition-colors p-1"
                title="Ayuda"
                onClick={(e) => e.stopPropagation()}
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 border-t px-4 py-3 flex items-center justify-end gap-2 bg-card">
            {footer}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Help Card ───────────────────────────────────────────────────────────────

interface CrudHelpCardProps {
  title: string;
  children: ReactNode;
  className?: string;
}

export function CrudHelpCard({ title, children, className }: CrudHelpCardProps) {
  return (
    <div className={cn("rounded-lg border border-info/20 bg-info/10 p-3 text-sm", className)}>
      <p className="font-medium text-info mb-1">{title}</p>
      <div className="text-text-secondary space-y-1 [&>p]:text-xs [&>ul]:text-xs [&>ul]:list-disc [&>ul]:pl-4 [&>ul]:space-y-0.5">
        {children}
      </div>
    </div>
  );
}

// ─── Field Help ──────────────────────────────────────────────────────────────

interface FieldHelpProps {
  children: ReactNode;
  className?: string;
}

export function FieldHelp({ children, className }: FieldHelpProps) {
  return (
    <p className={cn("text-xs text-muted-foreground leading-relaxed", className)}>
      {children}
    </p>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

interface CrudEmptyStateProps {
  icon?: ReactNode | React.ElementType<{ className?: string }>;
  title: string;
  description?: string;
  action?: ReactNode;
  filtered?: boolean;
  className?: string;
}

export function CrudEmptyState({ icon, title, description, action, filtered, className }: CrudEmptyStateProps) {
  // Lucide (y otros forwardRef) son objetos `{$$typeof, render}`, no functions —
  // renderizarlos como children crashea React. Solo un ReactElement ya montado
  // se usa tal cual; el resto se instancia con createElement.
  const iconNode =
    icon == null
      ? null
      : isValidElement(icon)
        ? icon
        : createElement(icon as ElementType<{ className?: string }>, { className: "h-8 w-8" });

  return (
    <div className={cn("flex flex-col items-center justify-center py-16 px-4 text-center gap-3", className)}>
      {iconNode ? <div className="text-muted-foreground">{iconNode}</div> : null}
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && <p className="text-xs text-muted-foreground max-w-xs">{description}</p>}
        {filtered && <p className="text-xs text-muted-foreground">Probá cambiando el filtro de búsqueda.</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Lifecycle Badge ─────────────────────────────────────────────────────────

type LifecycleStatus = "draft" | "published" | "archived" | "active" | "inactive" | "pending";

const LIFECYCLE_CLASSES: Record<LifecycleStatus, string> = {
  draft:     "bg-warning/15 text-warning",
  published: "bg-success/15 text-success",
  archived:  "bg-muted text-muted-foreground",
  active:    "bg-success/15 text-success",
  inactive:  "bg-muted text-muted-foreground",
  pending:   "bg-info/15 text-info",
};

const LIFECYCLE_LABELS: Record<LifecycleStatus, string> = {
  draft: "Borrador", published: "Publicado", archived: "Archivado",
  active: "Activo", inactive: "Pausado", pending: "Pendiente",
};

interface LifecycleBadgeProps {
  status: string;
  className?: string;
}

export function LifecycleBadge({ status, className }: LifecycleBadgeProps) {
  const key = status as LifecycleStatus;
  const classes = LIFECYCLE_CLASSES[key] ?? "bg-muted text-muted-foreground";
  const label = LIFECYCLE_LABELS[key] ?? lifecycleStatusLabel(status);
  return (
    <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", classes, className)}>
      {label}
    </span>
  );
}
